import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FileText, Plus, AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';

const documentTypes = [
  { value: 'vehicle_insurance', label: 'Vehicle Insurance', category: 'Vehicle' },
  { value: 'vehicle_registration', label: 'Vehicle Registration (Istimara)', category: 'Vehicle' },
  { value: 'employee_insurance', label: 'Employee Insurance', category: 'Employee' },
  { value: 'iqama', label: 'Iqama', category: 'Employee' },
  { value: 'driving_license', label: 'Driving License', category: 'Employee' },
  { value: 'contract', label: 'Contract', category: 'Contract' },
  { value: 'other', label: 'Other', category: 'Other' }
];

export const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterExpiry, setFilterExpiry] = useState('all');

  const [formData, setFormData] = useState({
    document_type: '',
    reference_type: 'asset',
    reference_id: '',
    reference_name: '',
    expiry_date: '',
    notes: ''
  });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error('Document load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/documents', formData);
      toast.success('Document record created successfully!');
      setDialogOpen(false);
      loadDocuments();
      setFormData({
        document_type: '',
        reference_type: 'asset',
        reference_id: '',
        reference_name: '',
        expiry_date: '',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to create document');
    }
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', color: 'bg-red-100 text-red-800', label: 'Expired', days: Math.abs(daysUntilExpiry) };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'critical', color: 'bg-red-100 text-red-800', label: '⚠️ Critical', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 15) {
      return { status: 'warning', color: 'bg-amber-100 text-amber-800', label: 'Expiring Soon', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'attention', color: 'bg-yellow-100 text-yellow-800', label: 'Attention', days: daysUntilExpiry };
    } else {
      return { status: 'valid', color: 'bg-green-100 text-green-800', label: 'Valid', days: daysUntilExpiry };
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const docType = documentTypes.find(t => t.value === doc.document_type);
    const categoryMatch = filterCategory === 'all' || docType?.category === filterCategory;
    
    if (filterExpiry === 'all') return categoryMatch;
    
    const expiryStatus = getExpiryStatus(doc.expiry_date);
    if (filterExpiry === 'expiring' && expiryStatus && ['critical', 'warning', 'attention'].includes(expiryStatus.status)) {
      return categoryMatch;
    }
    if (filterExpiry === 'expired' && expiryStatus && expiryStatus.status === 'expired') {
      return categoryMatch;
    }
    
    return categoryMatch;
  });

  const expiringCount = documents.filter(doc => {
    const status = getExpiryStatus(doc.expiry_date);
    return status && ['critical', 'warning', 'attention'].includes(status.status);
  }).length;

  const expiredCount = documents.filter(doc => {
    const status = getExpiryStatus(doc.expiry_date);
    return status && status.status === 'expired';
  }).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="documents-title">Document Management</h1>
          <p className="text-muted-foreground mt-1">{documents.length} total documents</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="add-document-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Document
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Document Record</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Document Type *</Label>
                <Select value={formData.document_type} onValueChange={(v) => setFormData({ ...formData, document_type: v })} required>
                  <SelectTrigger data-testid="document-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label} ({type.category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Reference Type *</Label>
                  <Select value={formData.reference_type} onValueChange={(v) => setFormData({ ...formData, reference_type: v })} required>
                    <SelectTrigger data-testid="reference-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asset">Asset/Vehicle</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference ID *</Label>
                  <Input
                    value={formData.reference_id}
                    onChange={(e) => setFormData({ ...formData, reference_id: e.target.value })}
                    placeholder="Vehicle reg, employee ID"
                    required
                    data-testid="reference-id-input"
                  />
                </div>
              </div>
              <div>
                <Label>Reference Name</Label>
                <Input
                  value={formData.reference_name}
                  onChange={(e) => setFormData({ ...formData, reference_name: e.target.value })}
                  placeholder="Optional description"
                  data-testid="reference-name-input"
                />
              </div>
              <div>
                <Label>Expiry Date *</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  required
                  data-testid="expiry-date-input"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes"
                  data-testid="notes-input"
                />
              </div>
              <div className="text-sm text-muted-foreground bg-blue-50 p-3 rounded">
                <strong>Note:</strong> File upload feature coming soon. For now, track expiry dates and reference document locations.
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-button">Cancel</Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-document-button">
                  Add Record
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Documents</div>
          <div className="text-3xl font-bold" data-testid="total-documents">{documents.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Expiring Soon</div>
          <div className="text-3xl font-bold text-amber-600" data-testid="expiring-count">{expiringCount}</div>
          <div className="text-xs text-muted-foreground mt-1">Within 30 days</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Expired</div>
          <div className="text-3xl font-bold text-red-600" data-testid="expired-count">{expiredCount}</div>
          <div className="text-xs text-muted-foreground mt-1">Requires renewal</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Categories</div>
          <div className="text-3xl font-bold">{new Set(documents.map(d => documentTypes.find(t => t.value === d.document_type)?.category)).size}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[150px]" data-testid="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Vehicle">Vehicle</SelectItem>
                <SelectItem value="Employee">Employee</SelectItem>
                <SelectItem value="Contract">Contract</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Expiry Status</Label>
            <Select value={filterExpiry} onValueChange={setFilterExpiry}>
              <SelectTrigger className="w-[150px]" data-testid="filter-expiry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Documents</SelectItem>
                <SelectItem value="expiring">Expiring Soon</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading documents...</div>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No documents found</h3>
          <p className="text-muted-foreground mb-4">Start tracking important document expiry dates</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocuments.map((doc) => {
            const docType = documentTypes.find(t => t.value === doc.document_type);
            const expiryStatus = getExpiryStatus(doc.expiry_date);
            
            return (
              <Card key={doc.id} className="p-6 hover:shadow-lg transition-shadow duration-200" data-testid={`document-card-${doc.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-accent/10 rounded-lg">
                      <FileText className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-bold" data-testid={`document-type-${doc.id}`}>{docType?.label || doc.document_type}</h3>
                      <p className="text-xs text-muted-foreground">{docType?.category}</p>
                    </div>
                  </div>
                  {expiryStatus && (
                    <Badge className={expiryStatus.color} data-testid={`document-status-${doc.id}`}>
                      {expiryStatus.label}
                    </Badge>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{doc.reference_type}:</span>
                    <span className="font-semibold">{doc.reference_id}</span>
                  </div>
                  {doc.reference_name && (
                    <div className="text-xs text-muted-foreground">{doc.reference_name}</div>
                  )}
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-muted-foreground">Expiry Date:</span>
                    <span className="font-semibold">{new Date(doc.expiry_date).toLocaleDateString()}</span>
                  </div>
                  {expiryStatus && (
                    <div className={`text-xs font-semibold ${
                      expiryStatus.status === 'expired' ? 'text-red-600' :
                      expiryStatus.status === 'critical' ? 'text-red-600' :
                      expiryStatus.status === 'warning' ? 'text-amber-600' :
                      'text-green-600'
                    }`}>
                      {expiryStatus.status === 'expired' 
                        ? `Expired ${expiryStatus.days} days ago`
                        : `${expiryStatus.days} days remaining`
                      }
                    </div>
                  )}
                  {doc.notes && (
                    <div className="text-xs text-muted-foreground pt-2 border-t">{doc.notes}</div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
